import React, { FC, ReactNode } from "react";
import classNames from "classnames";

import { CopyButton, Icon, MessageArea } from "../../common";
import { ReactComponent as ArrowImg } from "../../../assets/illustrations/curved-arrow.svg";
import {
  CURRENCY_MAP,
  DEFAULT_FIAT_CURRENCY,
  INCOMING_PAYMENT_DETAILS_CONFIG,
} from "../../../constants";
import { Currency, IncomingPaymentDetails } from "../../../types/InvoiceTypes";
import { UIStatus } from "../../../types/GlobalTypes";

type Props = {
  show: boolean;
  message?: ReactNode;
  currency?: Currency;
  data?: IncomingPaymentDetails;
  removeReference?: boolean;
};

export const ArchwayDetails: FC<Props> = ({
  show,
  message,
  currency,
  data,
  removeReference,
}) => {
  const currencyFeatures =
    CURRENCY_MAP[currency || DEFAULT_FIAT_CURRENCY]?.features || [];

  if (removeReference) {
    const referenceIndex = currencyFeatures.indexOf("reference");
    if (referenceIndex !== -1) {
      currencyFeatures.splice(referenceIndex, 1);
    }
  }

  return (
    <MessageArea type={UIStatus.SUCCESS}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-x-2">
          <div className="hidden md:block">
            <Icon icon="Bank" size={24} className="text-gray-500" disableFill />
          </div>

          <div className="theme-caption text-gray-500 uppercase">
            Payment details
          </div>
        </div>

        {show && message && (
          <div className="w-full theme-subheading">{message}</div>
        )}

        <div className="flex">
          <div className="basis-2/3 flex flex-col gap-2">
            {currencyFeatures.map(
              (feature) =>
                ((!show &&
                  !INCOMING_PAYMENT_DETAILS_CONFIG[feature].hideByDefault) ||
                  (data && data[feature])) && (
                  <PaymentDetailsItemByConfig
                    key={feature}
                    feature={feature}
                    value={data ? data[feature] : undefined}
                    show={show}
                  />
                )
            )}
          </div>

          <div className="hidden md:flex basis-1/3 flex-auto justify-center items-center">
            <ArrowImg className="transform scale-x-[-1]" />
          </div>
        </div>
      </div>
    </MessageArea>
  );
};

type PaymentDetailsItemProps = {
  label: string;
  labelClassName?: string;
  value?: string;
  valueClassName?: string;
  hideValue?: boolean;
  showCopyButton?: boolean;
};

const PaymentDetailsItem: FC<PaymentDetailsItemProps> = ({
  label,
  labelClassName,
  value,
  valueClassName,
  hideValue = false,
  showCopyButton = false,
}) => {
  return (
    <div className="flex flex-row items-center">
      <div
        className={classNames(
          "basis-1/3 flex-none uppercase theme-subheading text-sm",
          labelClassName
        )}
      >
        {label}
      </div>

      {showCopyButton && <CopyButton value={value} />}

      <div
        className={classNames(
          "theme-text min-h-full flex items-center word-break",
          valueClassName,
          {
            "bg-green-100 overflow-hidden rounded-[40px] h-5 w-full":
              !!hideValue,
          }
        )}
      >
        {!hideValue ? value || "-" : ""}
      </div>
    </div>
  );
};

type PaymentDetailsItemByConfigProps = {
  feature: keyof IncomingPaymentDetails;
  value?: string;
  show: boolean;
};

const PaymentDetailsItemByConfig: FC<PaymentDetailsItemByConfigProps> = ({
  feature,
  value,
  show,
}) => {
  const config = INCOMING_PAYMENT_DETAILS_CONFIG[feature];

  return (
    <PaymentDetailsItem
      label={config?.label}
      labelClassName={config?.labelClassName}
      value={config?.format ? config?.format(value) : value}
      valueClassName={config?.valueClassName}
      hideValue={
        config?.alwaysVisible !== undefined ? !config.alwaysVisible : !show
      }
      showCopyButton={show}
    />
  );
};
